import { useState, useCallback, useEffect, useRef } from 'react';
import { Stack, Text, Group, Box, Button, Textarea } from '@mantine/core';
import { useConversationRenameActions } from '../../../../../hooks';
import classes from './RenameModal.module.css';

interface RenameModalProps {
  conversationId: string;
  currentTitle: string;
  onRename?: (newTitle: string) => void;
  onCancel?: () => void;
}

export const RenameModal = ({ conversationId, currentTitle, onRename, onCancel }: RenameModalProps) => {
  const [inputValue, setInputValue] = useState(currentTitle);
  const { renameConversation, isLoading, error, isSuccess, canRename } = useConversationRenameActions();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const syncOverflowState = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    const isOverflowing = textarea.scrollHeight > textarea.clientHeight + 1;
    textarea.style.overflowY = isOverflowing ? 'auto' : 'hidden';
  }, []);

  // Close modal on success
  useEffect(() => {
    if (isSuccess) {
      onRename?.(inputValue.trim());
      onCancel?.();
    }
  }, [isSuccess, onCancel, onRename, inputValue]);

  useEffect(() => {
    const frame = requestAnimationFrame(syncOverflowState);
    return () => cancelAnimationFrame(frame);
  }, [inputValue, syncOverflowState]);

  useEffect(() => {
    syncOverflowState();
  }, [syncOverflowState]);

  const handleRename = useCallback(async () => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue || trimmedValue === currentTitle) {
      onCancel?.();
      return;
    }
    
    try {
      await renameConversation(conversationId, trimmedValue);
      // Success is handled by useEffect above
    } catch (error) {
      // Error is handled by the hook, just log for debugging
      console.error('Rename failed:', error);
    }
  }, [inputValue, currentTitle, conversationId, renameConversation, onCancel]);

  const handleCancel = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleRename();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancel();
    }
  }, [handleRename, handleCancel]);

  return (
    <Box className={classes.modal}>
      <Stack gap={18}>
        {/* Title */}
        <Text className={classes.title}>Rename this chat</Text>
        
        {/* Input Field */}
        <Textarea
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          onKeyDown={handleKeyDown}
          autosize
          minRows={2}
          maxRows={4}
          className={classes.textInput}
          aria-label="Conversation title"
          ref={textareaRef}
          autoFocus
          disabled={isLoading}
        />
        
        {/* Error Message */}
        {error && (
          <Text size="sm" c="red" className={classes.errorText}>
            {error instanceof Error ? error.message : 'Failed to rename conversation'}
          </Text>
        )}
        
        {/* Action Buttons */}
        <Group gap={18} justify="flex-end">
          <Button 
            variant="subtle"
            onClick={handleCancel}
            className={classes.cancelButton}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRename}
            className={classes.renameButton}
            disabled={!canRename || !inputValue.trim() || inputValue.trim() === currentTitle}
            loading={isLoading}
            styles={{
              root: {
                backgroundColor: 'var(--fonterra-border-accent)',
                color: 'var(--fonterra-text-on-accent)',
                border: '1px solid transparent',
                borderRadius: '6px',
                padding: '9px 24px',
                fontWeight: 500,
                lineHeight: '1.6em',
                height: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  backgroundColor: 'var(--fonterra-accent-positive-hover)',
                },
                '&:disabled': {
                  backgroundColor: 'var(--fonterra-accent-positive-disabled)',
                  color: 'var(--fonterra-text-on-accent)',
                  opacity: 0.7,
                  cursor: 'not-allowed',
                },
              },
            }}
          >
            Rename
          </Button>
        </Group>
      </Stack>
    </Box>
  );
};

export type { RenameModalProps };
